import React, { useEffect, useRef, useState } from 'react';
import { getCaptchaChallenge } from '../api/captchaAPI';
import { turnstileSiteKey } from '../../env.json';

// Business requirement: 5-character alphanumeric captcha. This is a UI hint only —
// the actual answer and its length are decided and checked entirely server-side
// (api-cgc/helpers/captchaManager.js), so nothing here is a security boundary.
const CAPTCHA_LENGTH = 5;

const ModalCaptcha = ({ onBookSelect, bookDetails, serverError, onServerErrorConsumed }) => {
  const [errorMessage, setErrorMessage] = useState('');
  // One entry per box ('' = empty). `captchaInput` (below) derives the combined
  // string every render so all the existing logic that expects a single string
  // (auto-submit length check, submitAnswer, the turnstileToken effect) keeps
  // working unchanged. Deriving via .join() only reconstructs the true string
  // once every box is filled — which is exactly when length === CAPTCHA_LENGTH
  // matters anyway, so a mid-sequence gap (e.g. pasting into a later box) can
  // never masquerade as a complete answer.
  const [chars, setChars] = useState(() => Array(CAPTCHA_LENGTH).fill(''));
  const captchaInput = chars.join('');
  const [captchaId, setCaptchaId] = useState(null);
  const [svgMarkup, setSvgMarkup] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const submitLockRef = useRef(false);
  const boxRefs = useRef([]);
  const captchaIdRef = useRef(null);

  const focusBox = (index) => {
    const el = boxRefs.current[index];
    if (el) el.focus();
  };

  useEffect(() => {
    // Static backdrop + no keyboard escape + no close button: the booking flow
    // that opened this modal is only resumed by passing (or explicitly failing)
    // the challenge below.
    $('#modalCaptcha').modal({
      backdrop: 'static',
      keyboard: false,
    });

    fetchChallenge();
    renderTurnstileWhenReady();

    // Cleanup: the parent unmounts us by flipping `captcha` to false as soon as
    // it knows the outcome (success or an unrelated failure), which can happen
    // in the same render as React removing this component's own DOM node — so
    // by the time this runs, `$('#modalCaptcha')` may already select nothing
    // and Bootstrap's own hide() becomes a no-op. That would leave the
    // `modal-open` class/inline styles it applied to <body>, plus its backdrop
    // div (both live outside this component's DOM, under document.body),
    // stuck behind — which blocks the next modal (e.g. the add-player
    // BookingModal) from being usable. Strip them directly and unconditionally
    // instead of depending on Bootstrap's teardown handler firing.
    return () => {
      $('#modalCaptcha').modal('hide');
      $('body').removeClass('modal-open').css({ overflow: '', paddingRight: '' });
      $('.modal-backdrop').remove();

      // Best-effort only: the container (and its Turnstile iframe) may already be
      // gone by the time this runs, and letting this throw here would interrupt
      // the rest of this cleanup/commit — which previously left the add-player
      // BookingModal unable to open. Never let this step risk that again.
      try {
        if (window.turnstile && turnstileWidgetIdRef.current) {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        }
      } catch (err) {
        console.warn('Turnstile widget cleanup skipped:', err.message);
      }
    };
  }, []);

  // Drive focus off captchaId itself rather than a timing guess: this fires
  // right after fetchChallenge() succeeds, both on the initial mount fetch and
  // on the serverError-triggered refetch below (the modal is already visible
  // by then, so focusing immediately just works).
  useEffect(() => {
    captchaIdRef.current = captchaId;
    if (captchaId) focusBox(0);
  }, [captchaId]);

  // Safety net for the initial mount: if the modal's own fade-in transition
  // finishes (and steals focus) after captchaId was already set, re-focus the
  // first box once Bootstrap says the modal is actually shown.
  useEffect(() => {
    const handleShown = () => {
      if (captchaIdRef.current) focusBox(0);
    };
    $('#modalCaptcha').on('shown.bs.modal', handleShown);
    return () => {
      $('#modalCaptcha').off('shown.bs.modal', handleShown);
    };
  }, []);

  // The parent (bookingSheet.js) sets this when the server rejects a submitted
  // answer (wrong / expired / already used, on either the captcha text or the
  // Turnstile token). We surface the message and pull a brand new challenge —
  // both the captchaId and the Turnstile token are single-use server-side, so
  // retrying with either the same one can never succeed.
  useEffect(() => {
    if (serverError) {
      setErrorMessage(serverError);
      setChars(Array(CAPTCHA_LENGTH).fill(''));
      fetchChallenge();
      resetTurnstile();
      if (onServerErrorConsumed) onServerErrorConsumed();
    }
  }, [serverError]);

  // If the 5-char answer was typed before Turnstile's invisible check finished
  // (it usually completes in under a second, but shouldn't block typing), submit
  // as soon as the token arrives instead of forcing the user to retype anything.
  useEffect(() => {
    if (turnstileToken && captchaInput.length === CAPTCHA_LENGTH) {
      submitAnswer(captchaInput, turnstileToken);
    }
    // eslint-disable-next-line
  }, [turnstileToken]);

  const renderTurnstileWhenReady = () => {
    const tryRender = () => {
      if (!window.turnstile || !turnstileContainerRef.current) return false;
      if (turnstileWidgetIdRef.current) return true;

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
      return true;
    };

    if (tryRender()) return;

    // The script tag loads async; poll briefly until window.turnstile is available.
    const pollId = setInterval(() => {
      if (tryRender()) clearInterval(pollId);
    }, 200);
    setTimeout(() => clearInterval(pollId), 10000);
  };

  const resetTurnstile = () => {
    setTurnstileToken('');
    if (window.turnstile && turnstileWidgetIdRef.current) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
  };

  const fetchChallenge = async () => {
    setIsLoading(true);
    setChars(Array(CAPTCHA_LENGTH).fill(''));
    try {
      const { captchaId: id, svg } = await getCaptchaChallenge();
      setCaptchaId(id);
      setSvgMarkup(svg);
    } catch (err) {
      setCaptchaId(null);
      setSvgMarkup('');
      setErrorMessage('Unable to load captcha. Tap refresh to try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Per-box change handler. maxLength=1 on the input keeps a normal keystroke
  // to a single character; e.target.value is only ever '' (cleared) or that
  // one character here — multi-character paste is handled separately below.
  const handleBoxChange = (index, e) => {
    const char = e.target.value ? e.target.value.slice(-1).toUpperCase() : '';

    const nextChars = [...chars];
    nextChars[index] = char;
    setChars(nextChars);
    if (errorMessage) setErrorMessage('');

    if (char && index < CAPTCHA_LENGTH - 1) focusBox(index + 1);

    const combined = nextChars.join('');
    if (combined.length === CAPTCHA_LENGTH && turnstileToken) {
      submitAnswer(combined, turnstileToken);
    }
    // If the token isn't ready yet, the effect above submits automatically
    // the moment it arrives — no separate branch needed here.
  };

  const handleBoxKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (chars[index]) return; // let the native clear fire onChange as usual
      if (index === 0) return;
      e.preventDefault();
      const prevIndex = index - 1;
      const nextChars = [...chars];
      nextChars[prevIndex] = '';
      setChars(nextChars);
      if (errorMessage) setErrorMessage('');
      focusBox(prevIndex);
    } else if (e.key === 'ArrowLeft') {
      if (index > 0) {
        e.preventDefault();
        focusBox(index - 1);
      }
    } else if (e.key === 'ArrowRight') {
      if (index < CAPTCHA_LENGTH - 1) {
        e.preventDefault();
        focusBox(index + 1);
      }
    }
  };

  // Distributes a pasted string across boxes starting at whichever box
  // received the paste, capped at CAPTCHA_LENGTH total — same cap the old
  // single input enforced, just applied as a fill boundary instead of an
  // all-or-nothing rejection.
  const handleBoxPaste = (index, e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text) return;
    const pasted = text.toUpperCase();

    const nextChars = [...chars];
    let pos = index;
    for (let i = 0; i < pasted.length && pos < CAPTCHA_LENGTH; i += 1) {
      nextChars[pos] = pasted[i];
      pos += 1;
    }
    setChars(nextChars);
    if (errorMessage) setErrorMessage('');
    focusBox(Math.min(pos, CAPTCHA_LENGTH - 1));

    const combined = nextChars.join('');
    if (combined.length === CAPTCHA_LENGTH && turnstileToken) {
      submitAnswer(combined, turnstileToken);
    }
  };

  const submitAnswer = async (answer, token) => {
    if (!captchaId || submitLockRef.current) return;
    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      // onBookSelect (bookingSheet.js) re-submits blockTeeTime with this proof.
      // It handles both outcomes itself: on success it closes this modal
      // (setCaptcha(false)); on an invalid/expired answer it re-opens with
      // `serverError` set, which the effect above picks up.
      await onBookSelect(bookDetails, { captchaId, captchaText: answer, turnstileToken: token });
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal fade modal-responsive" id="modalCaptcha" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            {/* No close button to prevent closing */}
          </div>
          <div className="modal-body text-center">
            <div id="ModalCaptchaHolder">
              <div id="messageCaptchaModel"></div>
              {/* <p className="cgc-captcha-instructions">
                Please type the 5 characters shown below to continue your booking.
              </p> */}
              <div className="cgc-captcha-row">
                <div className="cgc-captcha-image">
                  {isLoading && !svgMarkup && <div className="cgc-captcha-loading">Loading…</div>}
                  <div dangerouslySetInnerHTML={{ __html: svgMarkup }} />
                </div>
                <button
                  type="button"
                  onClick={fetchChallenge}
                  disabled={isLoading || isSubmitting}
                  className="btn btn-success cgc-captcha-refresh"
                  title="Get a new code"
                  aria-label="Get a new captcha code"
                >
                  <i className="fa fa-refresh" aria-hidden="true"></i>
                  <span>New Code</span>
                </button>
              </div>
              <div id="captchaForm" role="form">
                <div className="form-group">
                  <div
                    className="cgc-captcha-boxes"
                    role="group"
                    aria-label="Enter the 5-character captcha code"
                  >
                    {chars.map((char, index) => (
                      <input
                        key={index}
                        ref={(el) => (boxRefs.current[index] = el)}
                        name={`txtCode${index}`}
                        type="text"
                        inputMode="text"
                        maxLength={1}
                        className={`form-control cgc-captcha-box ${errorMessage ? ' cgc-captcha-box-error' : ''}`}
                        style={{ fontSize: '36px', border: "2px solid #dfdfdd",borderRadius: '10px'}}
                        value={char}
                        onChange={(e) => handleBoxChange(index, e)}
                        onKeyDown={(e) => handleBoxKeyDown(index, e)}
                        onPaste={(e) => handleBoxPaste(index, e)}
                        disabled={isLoading || isSubmitting || !captchaId}
                        autoComplete="off"
                      />
                    ))}
                  </div>
                </div>
                <div ref={turnstileContainerRef} className="cgc-turnstile-container"></div>
                {captchaInput.length === CAPTCHA_LENGTH && !turnstileToken && !isSubmitting && (
                  <div className="text-muted">Verifying your browser, please wait...</div>
                )}
                {isSubmitting && <div className="text-muted">Verifying...</div>}
                {errorMessage && <div className="text-danger cgc-captcha-error">{errorMessage}</div>}
              </div>
            </div>
          </div>
          <style>{`
            .cgc-captcha-instructions {
              font-size: 16px;
              margin-bottom: 14px;
            }
            .cgc-captcha-row {
              display: flex;
              flex-wrap: wrap;
              align-items: center;
              justify-content: center;
              gap: 14px;
              margin-bottom: 18px;
            }
            .cgc-captcha-image {
              width: 100%;
              max-width: 320px;
              aspect-ratio: 260 / 100;
              background: #f7f7f5;
              border: 2px solid #bbbbbb;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
            }
            .cgc-captcha-image svg {
              width: 100%;
              height: 100%;
              display: block;
            }
            .cgc-captcha-loading {
              font-size: 15px;
              color: #666;
            }
            .cgc-captcha-refresh {
              display: inline-flex;
              align-items: center;
              gap: 8px;
              min-height: 44px;
              padding: 10px 18px;
              border-radius: 22px;
              font-size: 16px;
              white-space: nowrap;
            }
            .cgc-captcha-refresh i {
              font-size: 18px;
            }
            .cgc-captcha-boxes {
              display: flex;
              justify-content: center;
              gap: 10px;
              max-width: 320px;
              margin: 0 auto;
            }
            .cgc-captcha-box {
              flex: 1 1 0;
              min-width: 0;
              text-align: center;
              text-transform: uppercase;
              font-weight: 600;
              height: 56px;
              padding: 0;
              font-size: 36px;
            }
            .cgc-captcha-box-error {
              border-color: #d9534f;
            }
            .cgc-captcha-box:focus {
              border-color: #368800 !important;
              outline: none;
            }
            .cgc-captcha-error {
              font-size: 15px;
              margin-top: 8px;
            }
            .cgc-turnstile-container {
              display: flex;
              justify-content: center;
              margin: 14px auto 0;
            }
          `}</style>
          <div className="modal-footer">
            {/* No submit button needed */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalCaptcha;
